<template>
    <ul :class="$style.cards">
        <li
            v-for="card in cardsWithPlaceHolders"
            :key="card?.hru"
        >
            <div
                v-if="!card"
                :class="$style.card"
            >
                <CardContainer
                    :src="undefined"
                    placeholder
                    :class="$style.image_override"
                    itemprop="image"
                />
                <AnimatedPlaceholder :class="$style.title_placeholder" />
            </div>
            <BaseLink
                v-else
                :class="$style.card"
                :to="getLink(card.routeName || ROUTE_NAMES.GAME_PAGE, card.hru)"
                @click="props.onClick"
            >
                <CardContainer
                    :src="card.preview"
                    :class="$style.image_override"
                    itemprop="image"
                >
                    <Icon
                        v-if="card.previewIcon"
                        :name="card.previewIcon"
                        :class="$style.preview_icon"
                    />
                </CardContainer>
                <Typography
                    type="label"
                    size="m"
                    :class="$style.title"
                >
                    {{ card.title }}
                </Typography>
                <Icon
                    v-if="card.statusIcon"
                    :name="card.statusIcon"
                    :class="$style.icon"
                />
            </BaseLink>
        </li>
    </ul>
</template>
<script setup lang="ts">
import { computed } from 'vue'

import AnimatedPlaceholder from '@/components/AnimatedPlaceholder/AnimatedPlaceholder.vue'
import BaseLink from '@/components/BaseLink.vue'
import CardContainer from '@/components/CardContainer.vue'
import type { IconNameType } from '@/components/Icon'
import Icon from '@/components/Icon/Icon.vue'
import Typography from '@/components/Typography.vue'
import { ROUTE_NAMES, type ROUTE_NAMES_GAME_GROUPS } from '@/router/constants'
import { CLIENT_QUERY } from '@/router/query-params'

const props = defineProps<{
    cards?: {
        title: string
        hru: string
        routeName?: typeof ROUTE_NAMES.GAME_PAGE | ROUTE_NAMES_GAME_GROUPS
        statusIcon?: IconNameType
        preview?: string
        previewIcon?: IconNameType
    }[]
    searchQuery?: string
    onClick?: () => void
}>()

const cardsWithPlaceHolders = computed(() => {
    if (props.cards) {
        return props.cards
    }
    return Array<undefined>(3).fill(undefined)
})

function getLink(routeName: typeof ROUTE_NAMES.GAME_PAGE | ROUTE_NAMES_GAME_GROUPS, hru: string) {
    if (routeName === ROUTE_NAMES.GAME_PAGE) {
        const query = props.searchQuery
            ? { [CLIENT_QUERY.FROM]: 'portal_search', [CLIENT_QUERY.PORTAL_SEARCH_QUERY]: props.searchQuery }
            : undefined
        return { name: routeName, params: { game: hru }, query }
    }
    return { name: routeName, params: { hru } }
}
</script>
<style module>
.cards {
    display: flex;
    flex-direction: column;
    gap: 8px;
    box-sizing: border-box;
}

.card {
    display: flex;
    align-items: center;
    gap: 12px;
    box-sizing: border-box;
    color: var(--white);
}

.card:hover:not(:has(.preview_icon)),
.card:focus:not(:has(.preview_icon)),
.card:hover:not(:has(.preview_icon)) .icon,
.card:focus:not(:has(.preview_icon)) .icon {
    color: var(--purple-600);
}

.image_override {
    width: 100px;
    border-radius: 8px;
    flex-shrink: 0;
}

.card:hover .image_override,
.card:focus .image_override {
    background-color: var(--purple-500);
}

.preview_icon {
    margin: auto;
    width: 32px;
    height: 32px;
    color: var(--purple-500);
}

.card:hover .preview_icon,
.card:focus .preview_icon {
    color: var(--white);
}

.icon {
    width: 24px;
    height: 24px;
    color: var(--white-20);
    margin-left: auto;
    flex-shrink: 0;
}
.title_placeholder {
    height: 32px;
    width: 60%;
    border-radius: 16px;
}

@media (--desktop-small-plus) {
    .card {
        padding: 0;
    }

    .title {
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
    }
}
</style>
